
import { FORMATTING_INSTRUCTIONS } from './formattingInstructions';

// Implementation stage templates

export const implementationTemplates = {
  coreCapabilities: `${FORMATTING_INSTRUCTIONS}

Objective: Assess required capabilities and identify capacity gaps for successful execution of {campaign_name} campaign.

You are a senior campaign manager and organizational development specialist. Conduct comprehensive capability assessment and capacity building recommendations.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Winning Aspiration: {winning_aspiration}
- Timeline: {campaign_timeline}

Strategic Context:
- Required Capabilities: {required_capabilities}
- Coalition Opportunities: {coalition_opportunities}

Previous Analysis Context:
{previous_analysis}

Core Capabilities Assessment Tasks:

1. STRATEGIC CAPABILITIES AUDIT:
Assess current organizational strengths and gaps:

Research and Intelligence Capabilities:
- Political research and opposition tracking capacity
- Policy analysis and technical expertise
- Data collection and analysis infrastructure
- Media monitoring and communications tracking

Political Relationship Capabilities:
- Existing relationships with key decision makers
- Coalition partner relationships and trust levels
- Media relationships and communication channels
- Community organizing and grassroots connections

Communication and Messaging Capabilities:
- Message development and testing capacity
- Multi-platform communication infrastructure
- Rapid response and crisis communication systems
- Community-specific outreach and engagement

Organizational Management Capabilities:
- Project management and coordination systems
- Financial management and fundraising capacity
- Volunteer recruitment and management
- Legal and compliance expertise

2. CAPACITY GAP ANALYSIS:
Identify critical capability shortfalls:

High-Priority Gaps (Campaign-Critical):
- Capabilities essential for campaign success
- Gaps that cannot be filled through partnerships
- Time-sensitive capabilities needed immediately
- Capabilities requiring significant investment

Medium-Priority Gaps (Important but Manageable):
- Capabilities that enhance campaign effectiveness
- Gaps that can be filled through coalition partnerships
- Capabilities that can be developed during campaign
- Lower-cost capacity building opportunities

Low-Priority Gaps (Nice-to-Have):
- Capabilities that provide marginal advantage
- Gaps that can be addressed post-campaign
- Capabilities available through consultant relationships
- Long-term organizational development priorities

3. CAPABILITY DEVELOPMENT STRATEGY:
Plan for building required capabilities:

Internal Development:
- Staff hiring and skill development priorities
- Training and professional development programs
- System and infrastructure investments
- Organizational restructuring and role clarification

Coalition and Partnership Development:
- Partnership agreements for capability sharing
- Coalition role definition and coordination
- Resource sharing and joint capacity building
- Cross-organizational learning and development

External Resource Acquisition:
- Consultant and vendor relationship development
- Technology and infrastructure procurement
- Professional service partnerships
- Volunteer and intern program development

4. RESOURCE REQUIREMENTS AND ALLOCATION:
Calculate capacity building investment needs:

Personnel Resources:
- Core staff positions and skill requirements
- Consultant and contractor needs
- Volunteer recruitment and management resources
- Training and development investment

Financial Resources:
- Capability development budget requirements
- Technology and infrastructure costs
- Professional development and training expenses
- Partnership and coalition support costs

Time and Timeline Resources:
- Capability development timeline and milestones
- Training and onboarding schedule
- System implementation and testing phases
- Capability integration and coordination periods

5. PARTNERSHIP AND COALITION CAPACITY:
Leverage allied organization capabilities:

Coalition Member Capabilities:
- Existing capabilities within coalition organizations
- Capability sharing agreements and protocols
- Joint capacity building opportunities
- Resource pooling and coordination mechanisms

External Partner Capabilities:
- Professional organizations and expert networks
- Academic and research institution partnerships
- Media and communication support relationships
- Legal and advocacy organization alliances

6. CAPABILITY SUSTAINABILITY PLANNING:
Ensure long-term organizational development:

Post-Campaign Capability Retention:
- Core capabilities to maintain beyond campaign
- Staff retention and organizational continuity
- System and infrastructure maintenance
- Partnership relationship sustainability

Organizational Learning and Development:
- Campaign experience documentation and analysis
- Best practice development and sharing
- Organizational knowledge management
- Future campaign preparation and readiness

Provide detailed capability assessment with specific development recommendations and resource requirements.
`,

  campaignPlan: `${FORMATTING_INSTRUCTIONS}

Objective: Develop comprehensive campaign plan with management systems and operational framework for {campaign_name} campaign.

You are a senior campaign manager and operations director. Create detailed campaign plan with management systems, timelines, and operational procedures.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Winning Aspiration: {winning_aspiration}
- Timeline: {campaign_timeline}

Strategic Foundation:
- Theory of Change: {theory_of_change}
- Core Capabilities: {required_capabilities}

Previous Analysis Context:
{previous_analysis}

Campaign Plan Development Tasks:

1. COMPREHENSIVE CAMPAIGN TIMELINE:
Develop detailed project timeline with phases and milestones:

Phase 1: Foundation and Preparation (Month 1-2):
- Coalition building and partnership development
- Research completion and intelligence gathering
- Message testing and communication strategy finalization
- Organizational capacity building and staff onboarding
- Stakeholder mapping and relationship building initiation

Phase 2: Public Launch and Awareness Building (Month 2-4):
- Public campaign launch and media strategy execution
- Stakeholder engagement and coalition mobilization
- Community outreach and grassroots organizing
- Opposition monitoring and counter-strategy implementation
- Message amplification and public education

Phase 3: Political Pressure and Escalation (Month 4-6):
- Direct decision maker engagement and lobbying
- Public pressure campaigns and accountability actions
- Media pressure and investigative story development
- Coalition expansion and third-party endorsements
- Opposition response and counter-messaging

Phase 4: Decision Point Pressure and Victory (Month 6+):
- Intensive decision maker pressure and negotiation
- Deadline pressure and final mobilization
- Media coverage maximization and story amplification
- Coalition coordination for final push
- Victory preparation and implementation planning

2. ORGANIZATIONAL STRUCTURE AND MANAGEMENT:
Design campaign organization and governance:

Leadership Structure:
- Campaign director/manager roles and authority
- Strategic advisory committee composition and function
- Coalition coordination committee structure
- Decision-making processes and authority delegation

Functional Teams and Responsibilities:
- Research and intelligence team
- Communications and media team
- Political and stakeholder engagement team
- Grassroots organizing and mobilization team
- Operations and administration team

Management Systems and Processes:
- Regular meeting schedules and formats
- Communication protocols and information sharing
- Decision-making procedures and escalation paths
- Performance monitoring and adjustment processes
- Crisis response and rapid decision making

3. RESOURCE MANAGEMENT AND ALLOCATION:
Plan campaign resource requirements and deployment:

Budget Development and Management:
- Personnel costs and staff allocation
- Communication and media expenses
- Events and mobilization costs
- Research and consultant fees
- Technology and infrastructure investments

Fundraising and Resource Development:
- Coalition partner resource commitments
- Grant opportunities and foundation support
- Individual donor development and events
- In-kind resource acquisition and management
- Cost-sharing agreements and partnerships

Resource Allocation Strategy:
- Phase-based resource deployment
- Priority activity funding allocation
- Contingency reserves and flexibility planning
- Resource sharing with coalition partners
- Performance-based resource adjustment

4. STAKEHOLDER ENGAGEMENT STRATEGY:
Plan systematic stakeholder engagement and relationship management:

Primary Decision Maker Engagement:
- Individual stakeholder engagement plans
- Meeting schedules and relationship building
- Information sharing and persuasion strategy
- Follow-up and relationship maintenance
- Escalation and pressure tactics

Coalition and Partner Engagement:
- Regular coalition coordination and communication
- Joint activity planning and execution
- Resource sharing and capacity coordination
- Conflict resolution and unity maintenance
- Coalition expansion and recruitment

Community and Grassroots Engagement:
- Community meeting schedules and outreach
- Volunteer recruitment and management
- Event planning and mobilization activities
- Digital organizing and online engagement
- Local media and communication coordination

5. COMMUNICATION AND MEDIA STRATEGY:
Implement comprehensive communication plan:

Media Relations and Coverage:
- Press release schedule and story development
- Reporter relationship building and maintenance
- Editorial board meetings and opinion piece strategy
- Crisis communication and rapid response
- Social media strategy and digital engagement

Message Coordination and Discipline:
- Spokesperson training and message discipline
- Coalition message coordination and consistency
- Talking points development and distribution
- Message testing and refinement processes
- Opposition response and counter-messaging

Public Education and Awareness:
- Community presentation and speaking opportunities
- Educational material development and distribution
- Digital content creation and sharing
- Event-based communication and media generation
- Partnership amplification and third-party validation

6. MONITORING, EVALUATION, AND ADJUSTMENT:
Establish campaign tracking and improvement systems:

Performance Monitoring Systems:
- Key performance indicator tracking
- Regular progress assessment and reporting
- Stakeholder feedback collection and analysis
- Opposition activity monitoring and analysis
- Media coverage tracking and evaluation

Campaign Adjustment Processes:
- Regular strategy review and refinement
- Tactical adjustment based on performance data
- Resource reallocation based on effectiveness
- Timeline modification and deadline management
- Crisis response and strategic pivoting

Success Measurement and Evaluation:
- Milestone achievement tracking
- Coalition unity and engagement measurement
- Public opinion and awareness tracking
- Decision maker movement and commitment assessment
- Final outcome evaluation and lesson learning

Provide comprehensive campaign plan with detailed operational guidance and management systems.
`
};
